import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from './base_strategy';
import {
  BiconomyStrategy,
  BinanceStrategy,
  BingxStrategy,
  BitfinexStrategy,
  BitgetStrategy,
  BithumbStrategy,
  BitmartStrategy,
  BitstampStrategy,
  BitvavoStrategy,
  BullishStrategy,
  BybitStrategy,
  CoinbaseStrategy,
  CoinstoreStrategy,
  CoinwStrategy,
  CryptoComStrategy,
  DexTradeStrategy,
  DigiFinexStrategy,
  GateStrategy,
  GeminiStrategy,
  HashKeyStrategy,
  HotcoinStrategy,
  HtxStrategy,
  IndodaxStrategy,
  KrakenStrategy,
  KucoinStrategy,
  LbankStrategy,
  MexcStrategy,
  OkxStrategy,
  ToobitStrategy,
  UpbitStrategy,
  WeexStrategy,
  WhitebitStrategy,
  XtStrategy,
} from './strategies';

export const CexStrategy = ({
  cex,
  base,
  quote,
  orderbookDepth,
  onOrderbookUpdate,
  onTickerUpdate,
  onTradeUpdate,
  klineUpdate,
  precisionUpdate,
}: IInitArgs & { cex: ECexName }): BaseStrategy | undefined => {
  const properties = {
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  };

  if (cex === ECexName.gate) return new GateStrategy(properties);

  if (cex === ECexName.mexc) {
    return new MexcStrategy(properties);
  }

  if (cex === ECexName.binance) {
    return new BinanceStrategy(properties);
  }

  if (cex === ECexName.bingx) {
    return new BingxStrategy(properties);
  }

  if (cex === ECexName.kucoin) {
    return new KucoinStrategy(properties);
  }

  if (cex === ECexName.bitget) {
    return new BitgetStrategy(properties);
  }

  if (cex === ECexName.bithumb) {
    return new BithumbStrategy(properties);
  }

  if (cex === ECexName.okx) {
    return new OkxStrategy(properties);
  }

  if (cex === ECexName.bybit) {
    return new BybitStrategy(properties);
  }

  if (cex === ECexName.bitmart) {
    return new BitmartStrategy(properties);
  }

  if (cex === ECexName.bitstamp) {
    return new BitstampStrategy(properties);
  }

  if (cex === ECexName.bitvavo) {
    return new BitvavoStrategy(properties);
  }

  if (cex === ECexName.toobit) {
    return new ToobitStrategy(properties);
  }

  if (cex === ECexName.hashkey) {
    return new HashKeyStrategy(properties);
  }

  if (cex === ECexName.bullish) {
    return new BullishStrategy(properties);
  }

  if (cex === ECexName.gemini) {
    return new GeminiStrategy(properties);
  }

  if (cex === ECexName.bitfinex) {
    return new BitfinexStrategy(properties);
  }

  if (cex === ECexName.xt) {
    return new XtStrategy(properties);
  }

  if (cex === ECexName.lbank) {
    return new LbankStrategy(properties);
  }

  if (cex === ECexName.htx) {
    return new HtxStrategy(properties);
  }

  if (cex === ECexName.whitebit) {
    return new WhitebitStrategy(properties);
  }

  if (cex === ECexName.coinstore) {
    return new CoinstoreStrategy(properties);
  }

  if (cex === ECexName.coinbase) {
    return new CoinbaseStrategy(properties);
  }

  if (cex === ECexName.cryptocom) {
    return new CryptoComStrategy(properties);
  }

  if (cex === ECexName.upbit) {
    return new UpbitStrategy(properties);
  }

  if (cex === ECexName.coinw) {
    return new CoinwStrategy(properties);
  }

  if (cex === ECexName.indodax) {
    return new IndodaxStrategy(properties);
  }

  if (cex === ECexName.hotcoin) {
    return new HotcoinStrategy(properties);
  }

  if (cex === ECexName.kraken) {
    return new KrakenStrategy(properties);
  }

  if (cex === ECexName.weex) {
    return new WeexStrategy(properties);
  }

  if (cex === ECexName.biconomy) {
    return new BiconomyStrategy(properties);
  }

  if (cex === ECexName.dextrade) {
    return new DexTradeStrategy(properties);
  }

  if (cex === ECexName.digifinex) {
    return new DigiFinexStrategy(properties);
  }

  return undefined;
};

export const CexStrategyFactory = ({
  cex,
  base,
  quote,
  orderbookDepth,
  onOrderbookUpdate,
  onTickerUpdate,
  klineUpdate,
  precisionUpdate,
  onTradeUpdate,
}: IInitArgs & { cex: ECexName }): Promise<BaseStrategy | undefined> | undefined => {
  const properties = {
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  };

  if (cex === ECexName.gate) return GateStrategy.init(properties);

  if (cex === ECexName.mexc) {
    return MexcStrategy.init(properties);
  }

  if (cex === ECexName.binance) {
    return BinanceStrategy.init(properties);
  }

  if (cex === ECexName.bingx) {
    return BingxStrategy.init(properties);
  }

  if (cex === ECexName.kucoin) {
    return KucoinStrategy.init(properties);
  }

  if (cex === ECexName.bitget) {
    return BitgetStrategy.init(properties);
  }

  if (cex === ECexName.bithumb) {
    return BithumbStrategy.init(properties);
  }

  if (cex === ECexName.okx) {
    return OkxStrategy.init(properties);
  }

  if (cex === ECexName.bybit) {
    return BybitStrategy.init(properties);
  }

  if (cex === ECexName.bitmart) {
    return BitmartStrategy.init(properties);
  }

  if (cex === ECexName.bitstamp) {
    return BitstampStrategy.init(properties);
  }

  if (cex === ECexName.bitvavo) {
    return BitvavoStrategy.init(properties);
  }

  if (cex === ECexName.toobit) {
    return ToobitStrategy.init(properties);
  }

  if (cex === ECexName.hashkey) {
    return HashKeyStrategy.init(properties);
  }

  if (cex === ECexName.bullish) {
    return BullishStrategy.init(properties);
  }

  if (cex === ECexName.gemini) {
    return GeminiStrategy.init(properties);
  }

  if (cex === ECexName.bitfinex) {
    return BitfinexStrategy.init(properties);
  }

  if (cex === ECexName.xt) {
    return XtStrategy.init(properties);
  }

  if (cex === ECexName.lbank) {
    return LbankStrategy.init(properties);
  }

  if (cex === ECexName.htx) {
    return HtxStrategy.init(properties);
  }

  if (cex === ECexName.whitebit) {
    return WhitebitStrategy.init(properties);
  }

  if (cex === ECexName.coinstore) {
    return CoinstoreStrategy.init(properties);
  }

  if (cex === ECexName.coinbase) {
    return CoinbaseStrategy.init(properties);
  }

  if (cex === ECexName.cryptocom) {
    return CryptoComStrategy.init(properties);
  }

  if (cex === ECexName.upbit) {
    return UpbitStrategy.init(properties);
  }

  if (cex === ECexName.coinw) {
    return CoinwStrategy.init(properties);
  }

  if (cex === ECexName.indodax) {
    return IndodaxStrategy.init(properties);
  }

  if (cex === ECexName.hotcoin) {
    return HotcoinStrategy.init(properties);
  }

  if (cex === ECexName.kraken) {
    return KrakenStrategy.init(properties);
  }

  if (cex === ECexName.weex) {
    return WeexStrategy.init(properties);
  }

  if (cex === ECexName.biconomy) {
    return BiconomyStrategy.init(properties);
  }

  if (cex === ECexName.dextrade) {
    return DexTradeStrategy.init(properties);
  }

  if (cex === ECexName.digifinex) {
    return DigiFinexStrategy.init(properties);
  }

  return undefined;
};
